// src/components/PurchaseFlow/Step/OfferStep.js
import React, { useState, useEffect, useContext, useRef } from 'react';
import api from '../../../../services/api';
import Skeleton from 'react-loading-skeleton';
import NavigationButtons from '../../NavigationButtons';
import { formatNumber, parseLocaleNumber } from '../../../../utils/numberFormatter';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDollarSign, faBlender, faMobileAlt, faHome, faLaptop, faBox, faFileAlt, faInfoCircle, faMotorcycle } from '@fortawesome/free-solid-svg-icons';
import 'react-loading-skeleton/dist/skeleton.css';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import './OfferStep.css';
import './DownpaymentPlans.css';
import { SelectedOfferContext } from '../../../../context/SelectedOfferContext';
import { ClientContext } from '../../../../context/ClientContext';
import { useAuth } from '../../../../context/AuthContext';
import AuthorizationModal from '../../../PurchaseFlow/AuthorizationModal';
import { handleValidationErrors } from '../../../../utils/errorHandler';
import { trackInstallmentPlanSelected, trackOffersSearched } from '../../../../analytics/events';
import { useAnalyticsContext } from '../../../../analytics/useAnalyticsContext';

const MySwal = withReactContent(Swal);

const OfferStep = ({ onNext, clientData, setPurchaseRequestId, onIndustrySelect, resetOfferStep, productLimits, onClearIndustrySelection }) => {
  const { setSelectedOffer } = useContext(SelectedOfferContext);
  const { clientAuthorization } = useContext(ClientContext);
  const { commerceId, branchId: analyticsBranchId } = useAnalyticsContext();
  const [amount, setAmount] = useState('');
  const [displayAmount, setDisplayAmount] = useState('');
  const [maxAmountState, setMaxAmount] = useState(clientData?.available_credit || 0);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [authorizationData, setAuthorizationData] = useState(null); // Estado para autorización manual
  const [categories, setCategories] = useState([]);
  const [authorizationsEnabled] = useState(process.env.REACT_APP_ENABLE_AUTHORIZATIONS === 'true');
  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [lastCalculatedAmount, setLastCalculatedAmount] = useState('');
  const [showAuthorizationModal, setShowAuthorizationModal] = useState(false);
  const [requestedAmount, setRequestedAmount] = useState(null);
  const { channelId, branchId } = useAuth();

  const [productNameConfig, setProductNameConfig] = useState('required');
  const [productDescriptionConfig, setProductDescriptionConfig] = useState('optional');
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  
  // Actualizar maxAmountState cuando clientData cambie
  useEffect(() => {
    if (clientData?.available_credit !== undefined) {
      setMaxAmount(clientData.available_credit);
    }
  }, [clientData?.available_credit]);

  // Resetear categoría e importe cuando se hace click en "Cambiar producto"
  // Usar useRef para rastrear el último valor procesado y evitar resets innecesarios
  const lastResetOfferStepRef = useRef(0);
  useEffect(() => {
    if (resetOfferStep !== undefined && resetOfferStep > lastResetOfferStepRef.current) {
      // Solo resetear si el valor realmente aumentó (nuevo reset)
      // No resetear si ya hay una categoría seleccionada y productLimits está disponible
      if (!selectedCategory || !productLimits) {
        setSelectedCategory('');
        setAmount('');
        setDisplayAmount('');
        setSelectedPlan(null);
      }
      lastResetOfferStepRef.current = resetOfferStep;
    }
  }, [resetOfferStep, selectedCategory, productLimits]);

  // Función para determinar el industryKey de una categoría (misma lógica que handleCategorySelect)
  const getIndustryKeyForCategory = (category) => {
    if (!category) return null;
    
    // Prioridad 1: Usar alias si existe (coincide con product_limits del backend)
    if (category.alias) {
      return category.alias;
    } else if (typeof category.id === 'string') {
      // Prioridad 2: Si el ID es string, usarlo directamente
      return category.id;
    } else if (category.label) {
      // Prioridad 3: Si el ID es numérico, normalizar el label:
      return category.label.toLowerCase()
        .replace(/\s+/g, '') // Remover espacios completamente
        .normalize('NFD') // Normalizar caracteres especiales
        .replace(/[\u0300-\u036f]/g, ''); // Remover acentos
    }
    
    return category.id;
  };

  // Función para verificar si una categoría está deshabilitada
  const isCategoryDisabled = (category) => {
    // Verificar condiciones: productLimits definido, existe la key, y available_authorized = 0
    if (!productLimits || !category) {
      return false;
    }
    
    const industryKey = getIndustryKeyForCategory(category);
    if (!industryKey) {
      return false;
    }
    
    // Buscar el industry en product_limits con diferentes variaciones
    let foundIndustryKey = null;
    
    // 1. Intentar con el key exacto
    if (productLimits[industryKey]) {
      foundIndustryKey = industryKey;
    } else {
      // 2. Buscar por coincidencia parcial (case-insensitive)
      const availableKeys = Object.keys(productLimits);
      const normalizedInput = industryKey.toLowerCase().trim();
      
      // Buscar coincidencia exacta (case-insensitive)
      foundIndustryKey = availableKeys.find(key => 
        key.toLowerCase() === normalizedInput
      );
      
      // Si no hay coincidencia exacta, buscar por coincidencia parcial
      if (!foundIndustryKey) {
        foundIndustryKey = availableKeys.find(key => 
          key.toLowerCase().includes(normalizedInput) || 
          normalizedInput.includes(key.toLowerCase())
        );
      }
      
      // Si aún no se encuentra, intentar normalizar el input
      if (!foundIndustryKey) {
        const normalizedInputClean = normalizedInput.replace(/[\s_-]/g, '');
        foundIndustryKey = availableKeys.find(key => {
          const normalizedKey = key.toLowerCase().replace(/[\s_-]/g, '');
          return normalizedKey === normalizedInputClean || 
                 normalizedKey.includes(normalizedInputClean) ||
                 normalizedInputClean.includes(normalizedKey);
        });
      }
    }
    
    if (!foundIndustryKey || !productLimits[foundIndustryKey]) {
      return false;
    }
    
    // Usar el campo disabled del backend (más seguro y simple)
    const industryLimits = productLimits[foundIndustryKey];
    return industryLimits.disabled === true;
  };

  // Función para manejar selección de categoría/industry
  const handleCategorySelect = (categoryId) => {
    setSelectedCategory(categoryId);
    
    // Notificar al ProfilePage si existe el callback
    if (onIndustrySelect && typeof onIndustrySelect === 'function') {
      // Buscar la categoría completa para obtener más información
      const selectedCategoryData = categories.find(cat => cat.id === categoryId);
      
      // Determinar el key del industry (prioridad):
      // 1. Usar el campo "alias" si existe (viene del API y coincide con product_limits)
      // 2. Si el ID es string, usarlo directamente
      // 3. Si el ID es numérico, normalizar el label como fallback
      // 4. Si no hay categoría encontrada, usar el categoryId tal cual
      let industryKey = categoryId;
      if (selectedCategoryData) {
        // Prioridad 1: Usar alias si existe (coincide con product_limits del backend)
        if (selectedCategoryData.alias) {
          industryKey = selectedCategoryData.alias;
        } else if (typeof selectedCategoryData.id === 'string') {
          // Prioridad 2: Si el ID es string, usarlo directamente
          industryKey = selectedCategoryData.id;
        } else if (selectedCategoryData.label) {
          // Prioridad 3: Si el ID es numérico, normalizar el label:
          // - Convertir a lowercase
          // - Reemplazar espacios con nada
          // - Remover caracteres especiales si es necesario
          industryKey = selectedCategoryData.label.toLowerCase()
            .replace(/\s+/g, '') // Remover espacios completamente
            .normalize('NFD') // Normalizar caracteres especiales
            .replace(/[\u0300-\u036f]/g, ''); // Remover acentos
        }
      }
      
      // Pasar tanto el key como el label al ProfilePage
      onIndustrySelect({
        key: industryKey,
        label: selectedCategoryData?.label || industryKey
      });
    }
  };
  
  useEffect(() => {
    const loadCategoriesFromAPI = async () => {
      try {
        const response = await api.get('/industry-categories');
        if (response.data.success && response.data.data.length > 0) {
          return response.data.data.map(category => {
            // Mapear iconos basado en el nombre del icono del backend
            const icon = {
              'faBlender': faBlender,
              'fa-solid fa-mobile': faMobileAlt,
              'fa-solid fa-home': faHome,
              'fa-solid fa-laptop': faLaptop,
              'fa-solid fa-motorcycle': faMotorcycle
            }[category.icon] || faBox; // faBox como fallback

            return {
              id: category.id,
              label: category.label,
              alias: category.alias, // Campo alias para mapear con product_limits
              icon: icon
            };
          });
        }
        return null;
      } catch (error) {
        console.warn('Failed to load categories from API, falling back to environment variables:', error.message);
        return null;
      }
    };

    const loadCategoriesFromEnv = () => {
      const categoriesConfig = process.env.REACT_APP_PRODUCT_CATEGORIES;
      if (!categoriesConfig) return [];

      return categoriesConfig.split(',').map(category => {
        const [id, label, iconName] = category.split(':');
        const icon = {
          faBlender,
          faMobileAlt,
          faHome,
          faLaptop
        }[iconName] || faBlender;

        return { id, label, icon };
      });
    };

    const loadCategories = async () => {
      setCategoriesLoading(true);
      
      // Intentar cargar desde API primero
      const apiCategories = await loadCategoriesFromAPI();
      
      if (apiCategories && apiCategories.length > 0) {
        console.log('Categories loaded from API');
        setCategories(apiCategories);
      } else {
        // Fallback a variables de entorno
        console.log('Categories loaded from environment variables');
        const envCategories = loadCategoriesFromEnv();
        setCategories(envCategories);
      }
      
      setCategoriesLoading(false);
    };

    // Cargar configuración de campos de producto
    const loadProductFieldConfig = () => {
      const nameConfig = process.env.REACT_APP_PRODUCT_NAME || 'required';
      const descriptionConfig = process.env.REACT_APP_PRODUCT_DESCRIPTION || 'optional';
      
      setProductNameConfig(nameConfig);
      setProductDescriptionConfig(descriptionConfig);
    };

    loadCategories();
    loadProductFieldConfig();
  }, []);

  const handleRequestAuthorization = async (payload) => {
    try {
      // Crear FormData para enviar archivo adjunto
      const formData = new FormData();
      formData.append('commerce_id', channelId);
      formData.append('channel_branch_id', branchId);
      formData.append('amount', payload.amount);
      formData.append('observation', payload.observation);
      
      if (payload.password) {
        formData.append('password', payload.password);
      }
      
      if (payload.attachment) {
        formData.append('attachment', payload.attachment);
      }

      const response = await api.post(`/customers/${clientData.id}/authorizations`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.status === 201) {
        setShowAuthorizationModal(false);
        
        MySwal.fire({
          title: 'Operación exitosa',
          text: response.data.message,
          icon: response.data.authorization.status === 'approved' ? 'success' : 'info',
        });

        if(response.data.authorization.status === 'approved') {
        }
        
        // Return response data so modal can track authorization status
        return response.data;
      }



      MySwal.fire({
        title: 'Autorización pendiente',
        text: 'Tu solicitud ha sido enviada. Por favor espera a que sea procesada.',
        icon: 'info',
      });
      
      // Return response data so modal can track authorization status
      return response.data;
    } catch (error) {
      console.error('Error al solicitar autorización:', error);
      const errorConfig = handleValidationErrors(error);
      MySwal.fire(errorConfig);
      throw error; // Re-lanzar el error para que el modal pueda resetear el estado de loading
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9,]/g, '');
    
    if (value === '') {
      setAmount('');
      setDisplayAmount('');
      setPlans([]);
      setSelectedPlan(null);
      setLastCalculatedAmount('');
      return;
    }

    // Convertir al formato español para mostrar
    const numericValue = parseLocaleNumber(value);
    if (!isNaN(numericValue)) {
      setAmount(numericValue.toString());
      setDisplayAmount(formatNumber(numericValue));
      setPlans([]);
      setSelectedPlan(null);
    }
  };

  const handleRequestMoreCredit = (e) => {
    e?.preventDefault();
    setRequestedAmount(null);
    setShowAuthorizationModal(true);
  };

  const handleAmountValidation = () => {
    const numericAmount = parseFloat(amount);
    if (!numericAmount || numericAmount <= 0) {
      MySwal.fire({
        icon: 'warning',
        title: 'Monto inválido',
        text: 'Por favor, ingresa un monto válido.',
      });
      return false;
    }

    if (numericAmount > maxAmountState) {
      if (authorizationsEnabled) {
        MySwal.fire({
          icon: 'warning',
          title: 'Monto excede el límite disponible',
          html: `
            El monto ingresado supera tu límite disponible por $${formatNumber(numericAmount - maxAmountState)}.<br><br>
            ¿Deseas solicitar una autorización por $${formatNumber(numericAmount)}?
          `,
          showConfirmButton: true,
          confirmButtonText: `Solicitar autorización por $${formatNumber(numericAmount)}`,
          showCancelButton: true,
          cancelButtonText: 'Cancelar'
        }).then((result) => {
          if (result.isConfirmed) {
            setRequestedAmount(numericAmount);
            setShowAuthorizationModal(true);
          }
        });
      } else {
        MySwal.fire({
          icon: 'error',
          title: 'Límite excedido',
          text: `El monto no puede superar el límite disponible de $${formatNumber(maxAmountState)}.`,
          confirmButtonText: 'Entendido'
        });
      }
      return false;
    }

    return true;
  };

  const fetchPlans = async () => {
    if (!handleAmountValidation()) return;

    // Validar que el monto haya cambiado
    if (amount === lastCalculatedAmount) {
      MySwal.fire({
        icon: 'info',
        title: 'Monto sin cambios',
        text: 'Modifica el monto para calcular nuevas cuotas.',
      });
      return;
    }

    if (!selectedCategory && categories.length > 0) {
      MySwal.fire({
        icon: 'warning',
        title: 'Categoría requerida',
        text: 'Por favor, selecciona una categoría de producto.',
      });
      return;
    }

    if (productNameConfig === 'required' && !productName.trim()) {
      MySwal.fire({
        icon: 'warning',
        title: 'Nombre del producto requerido',
        text: 'Por favor, ingresa el nombre del producto.',
      });
      return;
    }

    // Validación dinámica de la descripción del producto
    if (productDescriptionConfig === 'required' && !productDescription.trim()) {
      MySwal.fire({
        icon: 'warning',
        title: 'Descripción del producto requerida',
        text: 'Por favor, ingresa la descripción del producto.',
      });
      return;
    }

    setLoading(true);
    try {
      // Buscar la categoría seleccionada en el array de categorías para obtener su información
      const selectedCategoryData = categories.find(cat => cat.id === selectedCategory);
      
      const requestPayload = {
        amount: parseFloat(amount),
      };

      if (selectedCategoryData && selectedCategoryData.id) {
        requestPayload.industry_category_id = selectedCategoryData.id;
      }

      // Agregar campos de producto según la configuración
      if (productNameConfig !== 'hidden') {
        requestPayload.product_name = productName.trim() || null;
      }
      
      if (productDescriptionConfig !== 'hidden') {
        requestPayload.product_description = productDescription.trim() || null;
      }

      const response = await api.post(`/customers/${clientData.id}/purchase-requests`, requestPayload);

      // Track offers searched
      if (commerceId) {
        trackOffersSearched({
          commerceId,
          branchId: analyticsBranchId,
          amount: parseFloat(amount),
          productName: productName.trim() || undefined,
          productCategory: selectedCategoryData?.name || undefined,
          customerDni: clientData?.dni
        });
      }

      setPurchaseRequestId(response.data.id);
      const updatedOffers = response.data.offers.map((offer) => ({
        ...offer,
        purchase_request_id: response.data.id,
      }));

      // Separar planes regulares y planes con anticipo
      const regularPlans = updatedOffers.filter(plan => !plan.is_downpayment_plan);
      const downpaymentPlans = updatedOffers.filter(plan => plan.is_downpayment_plan);

      // Ordenar cada grupo por cantidad de cuotas
      const sortedRegularPlans = regularPlans.sort((a, b) => a.installments - b.installments);
      const sortedDownpaymentPlans = downpaymentPlans.sort((a, b) => a.installments - b.installments);

      // Combinar los grupos ordenados
      const sortedOffers = [...sortedRegularPlans, ...sortedDownpaymentPlans];

      setLastCalculatedAmount(amount); // Guardar el monto calculado
      setPlans(sortedOffers);
      setSelectedPlan(null); // Asegurar que no hay plan seleccionado

      if (sortedOffers.length === 0) {
        MySwal.fire({
          icon: 'info',
          title: 'No hay planes disponibles',
          html: `
            <div class="text-start">
              <p class="mb-3">
                En este momento no podemos ofrecerte planes de financiación para este monto.
                Esto puede deberse a diferentes motivos:
              </p>
              <ul class="list-unstyled">
                <li class="mb-2">
                  <i class="bi bi-circle-fill me-2 text-primary" style="font-size: 8px;"></i>
                  El monto solicitado supera tu límite máximo de cuota mensual disponible
                </li>
                <li class="mb-2">
                  <i class="bi bi-circle-fill me-2 text-primary" style="font-size: 8px;"></i>
                  El monto es inferior al mínimo requerido para financiar en cuotas
                </li>
                <li class="mb-2">
                  <i class="bi bi-circle-fill me-2 text-primary" style="font-size: 8px;"></i>
                  Existen restricciones temporales en tu perfil crediticio
                </li>
              </ul>
              <p class="mt-3">
                Te sugerimos:
              </p>
              <ul class="list-unstyled">
                <li class="mb-2">
                  <i class="bi bi-check2 me-2 text-success"></i>
                  Probar con un monto diferente
                </li>
                <li class="mb-2">
                  <i class="bi bi-check2 me-2 text-success"></i>
                  Consultar por otras opciones de pago
                </li>
                ${authorizationsEnabled ? `
                <li class="mb-2">
                  <i class="bi bi-check2 me-2 text-success"></i>
                  <a href="#" onclick="return false;" class="text-decoration-none" id="requestMoreCredit">
                    Solicitar una autorización especial
                  </a>
                </li>
                ` : ''}
              </ul>
            </div>
          `,
          confirmButtonText: 'Entendido',
          showCloseButton: true,
          didOpen: () => {
            if (authorizationsEnabled) {
              document.getElementById('requestMoreCredit').addEventListener('click', () => {
                MySwal.close();
                setShowAuthorizationModal(true);
              });
            }
          }
        });
      }
    } catch (error) {
      console.error('Error al crear la PurchaseRequest:', error);
      MySwal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron obtener los planes. Por favor, intenta nuevamente.',
      });
    } finally {
      setLoading(false);
    }
  };

  const selectPlan = (plan) => {
    setSelectedPlan(plan);
  };

  const handleNext = async () => {
    if (!selectedPlan) {
      Swal.fire({
        icon: 'warning',
        title: 'Selección requerida',
        text: 'Por favor, selecciona un plan antes de continuar.',
      });
      return;
    }

    // Validar que el monto actual coincida con el monto calculado
    if (amount !== lastCalculatedAmount) {
      Swal.fire({
        icon: 'warning',
        title: 'Monto modificado',
        text: 'El monto ha cambiado. Por favor, calcula las cuotas nuevamente.',
      });
      return;
    }

    try {
      const applicantId = clientData?.id;

      await api.post(
        `/customers/${applicantId}/purchase-requests/${selectedPlan.purchase_request_id}/select-offer`,
        { offer_id: selectedPlan.id }
      );

      // Guardar la oferta seleccionada en el contexto
      setSelectedOffer({
        amount: selectedPlan.financed_amount,
        originalAmount: parseFloat(amount),
        term: selectedPlan.installments,
        interestRate: selectedPlan.interest_rate,
        monthlyPayment: selectedPlan.installment_amount,
        firstDueDate: selectedPlan.first_due_date_formatted,
        displayDate: selectedPlan.display_date_formatted,
        isDownpaymentPlan: selectedPlan.is_downpayment_plan,
        downpaymentAmount: selectedPlan.downpayment_amount,
        downpaymentType: selectedPlan.downpayment_type,
        downpaymentValue: selectedPlan.downpayment_value,
      });

      // Track installment plan selected
      if (commerceId) {
        trackInstallmentPlanSelected({
          commerceId,
          branchId: analyticsBranchId,
          amount: selectedPlan.financed_amount,
          installments: selectedPlan.installments,
          interestRate: selectedPlan.interest_rate,
          customerDni: clientData?.dni,
          isDownpaymentPlan: selectedPlan.is_downpayment_plan || false,
          downpaymentType: selectedPlan.is_downpayment_plan ? selectedPlan.downpayment_type : undefined,
          downpaymentValue: selectedPlan.is_downpayment_plan ? selectedPlan.downpayment_value : undefined,
          downpaymentAmount: selectedPlan.is_downpayment_plan ? selectedPlan.downpayment_amount : undefined,
          originalAmount: selectedPlan.is_downpayment_plan ? parseFloat(amount) : undefined
        });
      }

      onNext({ selectedPlan });
    } catch (error) {
      console.error('Error al seleccionar la oferta:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Hubo un problema al seleccionar la oferta. Intenta nuevamente.',
      });
    }
  };

  const handleInputKeyPress = (e) => {
    if (e.key === 'Enter') {
      fetchPlans();
    }
  };

  const renderSkeletons = () => {
    return (
      <div className="row justify-content-center mt-5">
        {[...Array(3)].map((_, index) => (
          <div className="col-md-4" key={index}>
            <div className="card offer-option">
              <div className="card-body text-center">
                <Skeleton height={30} width={150} />
                <Skeleton height={20} width={100} className="mt-2" />
                <Skeleton height={20} width={180} className="mt-2" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderCategorySkeleton = () => {
    return (
      <div className="row justify-content-center">
        {[...Array(3)].map((_, index) => (
          <div className="col-md-3" key={index}>
            <div className="card category-option">
              <div className="card-body text-center">
                <Skeleton height={40} width={40} className="mb-3" />
                <Skeleton height={24} width={120} />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="row pt-3 d-flex justify-content-center">

      <div className="col-8 mb-0">
          {/* CTA "Cambiar producto" - Mismo eje vertical que Nombre, descripción, importe */}
          {selectedCategory && productLimits && (
            <div className="mb-1">
              <button
                className="btn btn-link p-0"
                onClick={() => {
                  setSelectedCategory('');
                  setAmount('');
                  setDisplayAmount('');
                  setSelectedPlan(null);
                  if (onClearIndustrySelection && typeof onClearIndustrySelection === 'function') {
                    onClearIndustrySelection();
                  }
                }}
                style={{
                  fontSize: '12px',
                  color: '#9CA3AF',
                  textDecoration: 'none',
                  border: 'none',
                  background: 'transparent',
                  fontWeight: '400'
                }}
                onMouseEnter={(e) => e.target.style.color = '#495057'}
                onMouseLeave={(e) => e.target.style.color = '#9CA3AF'}
              >
                <i className="bi bi-arrow-left me-1"></i>
                Cambiar producto
              </button>
            </div>
          )}
      </div>


      {/* Título siempre centrado */}
      <div className="col-12 mb-4">
        <h4 className="mb-0 text-center">¿Qué producto vas a vender?</h4>
      </div>

      {/* Skeleton de Categorías - Se muestra mientras se cargan */}
      {categoriesLoading && renderCategorySkeleton()}

      {/* Selector de Categorías - Solo se muestra si hay categorías configuradas y ya cargaron */}
      {!categoriesLoading && categories.length > 0 && !selectedCategory && (
        <div className="row justify-content-center">
          {categories.map((category) => {
            const isDisabled = isCategoryDisabled(category);
            return (
              <div className="col-md-3" key={category.id}>
                <div
                  className={`card category-option ${selectedCategory === category.id ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                  onClick={!isDisabled ? () => handleCategorySelect(category.id) : undefined}
                  style={{ cursor: isDisabled ? 'not-allowed' : 'pointer', opacity: isDisabled ? 0.6 : 1 }}
                >
                  <div className="card-body text-center">
                    <FontAwesomeIcon icon={category.icon} size="2x" className="mb-3" />
                    <h5 className="card-title">{category.label}</h5>
                    {isDisabled && (
                      <span className="badge bg-danger mt-2">Disponible Agotado</span>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Campos de producto y monto */}
      {!categoriesLoading && (selectedCategory || categories.length === 0) && (
        <div className="col-md-8">
          {/* Campo de nombre del producto - Solo se muestra si no está oculto */}
          {productNameConfig !== 'hidden' && (
            <div className="form-group mb-3">
              <div className="input-group">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faBox} />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Nombre del producto (ej.: Heladera Samsung)"
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                  required={productNameConfig === 'required'}
                />
              </div>
            </div>
          )}

          {/* Campo de descripción del producto - Solo se muestra si no está oculto */}
          {productDescriptionConfig !== 'hidden' && (
            <div className="form-group mb-3">
              <div className="input-group">
                <span className="input-group-text">
                  <FontAwesomeIcon icon={faFileAlt} />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder={`Descripción ${productDescriptionConfig === 'required' ? '(requerido)' : '(opcional)'}`}
                  value={productDescription}
                  onChange={(e) => setProductDescription(e.target.value)}
                  required={productDescriptionConfig === 'required'}
                />
              </div>
            </div>
          )}

          {/* Input de monto - Se muestra si hay categoría seleccionada o no hay categorías configuradas */}
          <div className="input-group">
            <span className="input-group-text">
              <FontAwesomeIcon icon={faDollarSign} />
            </span>
            <input
              type="text"
              className="form-control"
              id="amount"
              name="amount"
              autoComplete="off"
              placeholder="¿Cuál es el precio?"
              required
              value={displayAmount}
              onChange={handleAmountChange}
              onKeyPress={handleInputKeyPress}
            />
            <button 
              className="btn btn-primary" 
              type="button" 
              onClick={fetchPlans}
              disabled={amount === lastCalculatedAmount || !amount} // Deshabilitar si el monto no cambió o está vacío
            >
              Calcular Cuotas
            </button>
          </div>
          <p>
            <small className="text-muted">
              {`Monto máximo ${clientAuthorization?.status === 'approved' ? 'autorizado' : 'disponible'}: $${formatNumber(maxAmountState)}.`}
              {authorizationsEnabled && (
                <a href="#" onClick={handleRequestMoreCredit}>
                  Solicitar más cupo
                </a>
              )}
            </small>
          </p>
        </div>
      )}

      {loading ? (
        renderSkeletons()
      ) : (
        plans.length > 0 && (
          <>
            <div id="plansContainer" className="row justify-content-center mt-5">
              {/* Planes regulares */}
              {plans.filter(plan => !plan.is_downpayment_plan).map((plan) => (
                <div className="col-md-4" key={plan.id}>
                  <div 
                    className={`card offer-option ${selectedPlan?.id === plan.id ? 'selected' : ''} ${plan.is_downpayment_plan ? 'downpayment-plan' : ''}`} 
                    onClick={() => selectPlan(plan)} 
                    style={{ cursor: 'pointer' }}
                  >
                    <div className="card-body text-center">
                      {plan.is_downpayment_plan && plan.plan_name != '' && (
                        <div className="downpayment-plan-badge mb-2">
                          <span className="badge bg-primary">{plan.plan_name}</span>
                        </div>
                      )}
                      <h4 className="card-text">{plan.installments} Cuotas</h4>
                      <p className="card-title mb-3">
                        {`de $${formatNumber(plan.installment_amount)}`}
                      </p>
                      <p className="card-text">
                        {plan.is_downpayment_plan && plan.downpayment_type === 'installment' 
                          ? `${Math.min(plan.downpayment_value + 1, plan.installments)}ª cuota: ${plan.display_date_formatted}`
                          : `primera cuota: ${plan.display_date_formatted}`
                        }
                      </p>
                      
                      {plan.is_downpayment_plan && (
                        <div className="downpayment-plan-details mt-2">
                          <p className="mb-1 text-primary">
                            <small>
                              <strong>Pago Requerido</strong><br/>
                              {plan.downpayment_type === 'percentage' 
                                ? `${plan.downpayment_value}% del capital ($${formatNumber(plan.downpayment_amount)})`
                                : `${plan.downpayment_value} cuota${plan.downpayment_value > 1 ? 's' : ''} ($${formatNumber(plan.downpayment_amount)})`
                              }
                            </small>
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Contenedor independiente para planes con adelanto */}
            {plans.some(plan => plan.is_downpayment_plan) && (
              <div className="row justify-content-center mt-2">
                {plans.filter(plan => plan.is_downpayment_plan).map((plan) => (
                  <div className="col-md-4" key={plan.id}>
                    <div 
                      className={`card offer-option ${selectedPlan?.id === plan.id ? 'selected' : ''} ${plan.is_downpayment_plan ? 'downpayment-plan' : ''}`} 
                      onClick={() => selectPlan(plan)} 
                      style={{ cursor: 'pointer' }}
                    >
                      <div className="card-body text-center">
                        {plan.is_downpayment_plan && plan.plan_name != '' && (
                          <div className="downpayment-plan-badge mb-2">
                            <span className="badge bg-primary">{plan.plan_name}</span>
                          </div>
                        )}
                        <h4 className="card-text">{plan.installments} Cuotas</h4>
                        <p className="card-title mb-3">
                          {`de $${formatNumber(plan.installment_amount)}`}
                        </p>
                        <p className="card-text">
                          {plan.is_downpayment_plan && plan.downpayment_type === 'installment' 
                            ? `${Math.min(plan.downpayment_value + 1, plan.installments)}ª cuota: ${plan.display_date_formatted}`
                            : `primera cuota: ${plan.display_date_formatted}`
                          }
                        </p>
                        
                        {plan.is_downpayment_plan && (
                          <div className="downpayment-plan-details mt-2">
                            <p className="mb-1 text-primary">
                              <small>
                                <strong>Pago Requerido</strong><br/>
                                {plan.downpayment_type === 'percentage' 
                                  ? `${plan.downpayment_value}% del capital ($${formatNumber(plan.downpayment_amount)})`
                                  : `${plan.downpayment_value} cuota${plan.downpayment_value > 1 ? 's' : ''} ($${formatNumber(plan.downpayment_amount)})`
                                }
                              </small>
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Mensaje de regla de adelanto*/}
            {plans.some(plan => plan.is_downpayment_plan) && (
              <div className="row justify-content-center mt-3">
                <div className="col-md-12">
                  <div className="m-0 px-3 py-2 shadow rounded-2 text-primary" role="alert" style={{ background: '#f0f9ff'}}>
                    <div className="d-flex align-items-start">
                      <div>
                        <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
                        <strong>Atención</strong> 
                        <br/>
                        <p>Los planes que requieren anticipo deben abonarse en el momento de confirmar la compra.</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            <NavigationButtons onNext={handleNext} nextLabel="Continuar" />
            
          </>
        )
      )}

      <AuthorizationModal
        show={showAuthorizationModal}
        onClose={() => setShowAuthorizationModal(false)}
        onRequest={handleRequestAuthorization}
        initialAmount={requestedAmount}
        customerDni={clientData?.dni}
        reason={requestedAmount ? 'amount_exceeds_limit' : 'manual_request'}
      />
    </div>
  );
};

export default OfferStep;
